import React, { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

const UpgradePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = location.state?.from?.pathname || '/';

  const handleUpgrade = async () => {
    setLoading(true);
    setError(null);
    
    if (!user || !token) {
      setError(t('upgrade_failed'));
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/stripe/create-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: user.id,
          email: user.email,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('upgrade_failed'));
      }

      if (!data.url || typeof data.url !== 'string') {
        throw new Error(t('upgrade_failed'));
      }

      window.location.href = data.url;
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate(from);
  };

  if (!user || user.role !== 'free') {
    return <Navigate to="/" replace />;
  }

  return (
    <div style={{ maxWidth: 600, margin: 'auto', padding: 32, textAlign: 'center' }}>
      <h1>{t('upgrade_to_premium')}</h1>
      
      <div style={{ marginBottom: 32 }}>
        <p>{t('upgrade_message')}</p>
        <p>{t('upgrade_benefits')}</p>
      </div>

      <div style={{ 
        background: '#f5f5f5', 
        padding: 24, 
        borderRadius: 8, 
        marginBottom: 32,
        textAlign: 'left'
      }}>
        <h3>{t('premium_features')}:</h3>
        <ul>
          <li>{t('sync_across_devices')}</li>
          <li>{t('unlimited_codes')}</li>
          <li>{t('priority_support')}</li>
          <li>{t('advanced_security')}</li>
          <li>{t('backup_restore')}</li>
        </ul>
      </div>

      <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
        <button 
          onClick={handleUpgrade}
          disabled={loading}
          aria-label={t('upgrade_now')}
          style={{ 
            padding: '12px 24px', 
            fontSize: '16px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? t('processing') : t('upgrade_now')}
        </button>
        
        <button 
          onClick={handleGoBack}
          aria-label={t('go_back')}
          style={{ 
            padding: '12px 24px', 
            fontSize: '16px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer'
          }}
        >
          {t('go_back')}
        </button>
      </div>

      {error && (
        <div style={{ color: 'red', marginTop: 16 }} role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

export default UpgradePage; 